import { FC } from "react"
import { View, ViewStyle, TouchableOpacity } from "react-native"
import Animated, { useSharedValue, useAnimatedStyle, withSpring } from "react-native-reanimated"
import Icon from "react-native-vector-icons/MaterialIcons"

import { colors, spacing } from "@/theme"

interface StarRatingProps {
  rating: number
  onRatingChange: (rating: number) => void
  size?: number
  disabled?: boolean
  style?: ViewStyle
}

interface StarProps {
  index: number
  rating: number
  size: number
  disabled?: boolean
  onPress: (index: number) => void
}

const Star: FC<StarProps> = ({ index, rating, size, disabled, onPress }) => {
  const scale = useSharedValue(1)
  const isActive = index <= rating

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
  }))

  const handlePress = () => {
    if (disabled) return

    // Animate scale
    scale.value = withSpring(0.8, { damping: 15 }, () => {
      scale.value = withSpring(1, { damping: 15 })
    })

    onPress(index)
  }

  return (
    <TouchableOpacity
      onPress={handlePress}
      disabled={disabled}
      activeOpacity={0.8}
      style={$starButton}
    >
      <Animated.View style={animatedStyle}>
        <Icon name="star" size={size} color={isActive ? colors.primary : colors.tintInactive} />
      </Animated.View>
    </TouchableOpacity>
  )
}

export const StarRating: FC<StarRatingProps> = ({
  rating,
  onRatingChange,
  size = 40,
  disabled = false,
  style,
}) => {
  const handleStarPress = (index: number) => {
    onRatingChange(index)
  }

  return (
    <View style={[$container, style]}>
      {[1, 2, 3, 4, 5].map((index) => (
        <Star
          key={index}
          index={index}
          rating={rating}
          size={size}
          disabled={disabled}
          onPress={handleStarPress}
        />
      ))}
    </View>
  )
}

const $container: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  gap: spacing.xxs,
}

const $starButton: ViewStyle = {
  padding: spacing.xxxs / 2,
}
